import { fromJS } from 'immutable';
import { createSelector } from 'reselect';

// Action Types
export const types = {
    AGENCY_LOCATIONS_GET: 'AGENCY_LOCATIONS_GET',
    AGENCY_LOCATIONS_SUCCESS: 'AGENCY_LOCATIONS_SUCCESS',
    AGENCY_LOCATIONS_FAILURE: 'AGENCY_LOCATIONS_FAILURE',
    AGENCY_LOCATIONS_RESET: 'AGENCY_LOCATIONS_RESET',

    AGENCY_LOCATION_CREATE: 'AGENCY_LOCATION_CREATE',
    AGENCY_LOCATION_CREATE_SUCCESS: 'AGENCY_LOCATION_CREATE_SUCCESS',
    AGENCY_LOCATION_CREATE_FAILURE: 'AGENCY_LOCATION_CREATE_FAILURE',

    AGENCY_LOCATION_UPDATE: 'AGENCY_LOCATION_UPDATE',
    AGENCY_LOCATION_UPDATE_SUCCESS: 'AGENCY_LOCATION_UPDATE_SUCCESS',
    AGENCY_LOCATION_UPDATE_FAILURE: 'AGENCY_LOCATION_UPDATE_FAILURE',

    AGENCY_LOCATION_DELETE: 'AGENCY_LOCATION_DELETE',
    AGENCY_LOCATION_DELETE_SUCCESS: 'AGENCY_LOCATION_DELETE_SUCCESS',
    AGENCY_LOCATION_DELETE_FAILURE: 'AGENCY_LOCATION_DELETE_FAILURE',
};

// Action Creators
export const actions = {
    // Fetch agency locations
    getAgencyLocations: (premierAccessGroupId, params = {}) => ({
        type: types.AGENCY_LOCATIONS_GET,
        payload: { premierAccessGroupId, params },
    }),

    getAgencyLocationsSuccess: (data) => ({
        type: types.AGENCY_LOCATIONS_SUCCESS,
        payload: data,
    }),

    getAgencyLocationsFailure: (error) => ({
        type: types.AGENCY_LOCATIONS_FAILURE,
        payload: error,
    }),

    resetAgencyLocations: () => ({
        type: types.AGENCY_LOCATIONS_RESET,
    }),

    // Create agency location
    createAgencyLocation: (locationData) => ({
        type: types.AGENCY_LOCATION_CREATE,
        payload: locationData,
    }),

    createAgencyLocationSuccess: (location) => ({
        type: types.AGENCY_LOCATION_CREATE_SUCCESS,
        payload: location,
    }),

    createAgencyLocationFailure: (error) => ({
        type: types.AGENCY_LOCATION_CREATE_FAILURE,
        payload: error,
    }),

    // Update agency location
    updateAgencyLocation: (locationId, locationData) => ({
        type: types.AGENCY_LOCATION_UPDATE,
        payload: { locationId, locationData },
    }),

    updateAgencyLocationSuccess: (location) => ({
        type: types.AGENCY_LOCATION_UPDATE_SUCCESS,
        payload: location,
    }),

    updateAgencyLocationFailure: (error) => ({
        type: types.AGENCY_LOCATION_UPDATE_FAILURE,
        payload: error,
    }),

    // Delete agency location
    deleteAgencyLocation: (locationId) => ({
        type: types.AGENCY_LOCATION_DELETE,
        payload: { locationId },
    }),

    deleteAgencyLocationSuccess: (locationId) => ({
        type: types.AGENCY_LOCATION_DELETE_SUCCESS,
        payload: { locationId },
    }),

    deleteAgencyLocationFailure: (error) => ({
        type: types.AGENCY_LOCATION_DELETE_FAILURE,
        payload: error,
    }),
};

// Initial State
const initialState = fromJS({
    locations: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    perPage: 20,

    // CRUD operation states
    creating: false,
    createError: null,
    updating: false,
    updateError: null,
    deleting: false,
    deleteError: null,
});

// Reducer
const agencyLocations = (state = initialState, action) => {
    switch (action.type) {
    // Fetch agency locations
    case types.AGENCY_LOCATIONS_GET:
        return state.merge({
            loading: true,
            error: null,
        });

    case types.AGENCY_LOCATIONS_SUCCESS:
        return state.merge({
            loading: false,
            error: null,
            locations: fromJS(action.payload.data || action.payload.agency_locations || []),
            total: action.payload.total || 0,
            page: action.payload.page || 1,
            perPage: action.payload.perPage || 20,
        });

    case types.AGENCY_LOCATIONS_FAILURE:
        return state.merge({
            loading: false,
            error: action.payload,
            locations: fromJS([]),
        });

    case types.AGENCY_LOCATIONS_RESET:
        return initialState;

        // Create agency location
    case types.AGENCY_LOCATION_CREATE:
        return state.merge({
            creating: true,
            createError: null,
        });

    case types.AGENCY_LOCATION_CREATE_SUCCESS:
        return state.merge({
            creating: false,
            createError: null,
            locations: state.get('locations').push(fromJS(action.payload)),
            total: state.get('total') + 1,
        });

    case types.AGENCY_LOCATION_CREATE_FAILURE:
        return state.merge({
            creating: false,
            createError: action.payload,
        });

        // Update agency location
    case types.AGENCY_LOCATION_UPDATE:
        return state.merge({
            updating: true,
            updateError: null,
        });

    case types.AGENCY_LOCATION_UPDATE_SUCCESS: {
        const updatedLocation = fromJS(action.payload);
        const locations = state.get('locations');
        const index = locations.findIndex((loc) => loc.get('id') === updatedLocation.get('id'));

        return state.merge({
            updating: false,
            updateError: null,
            locations: index >= 0 ? locations.set(index, updatedLocation) : locations,
        });
    }

    case types.AGENCY_LOCATION_UPDATE_FAILURE:
        return state.merge({
            updating: false,
            updateError: action.payload,
        });

        // Delete agency location
    case types.AGENCY_LOCATION_DELETE:
        return state.merge({
            deleting: true,
            deleteError: null,
        });

    case types.AGENCY_LOCATION_DELETE_SUCCESS: {
        const locationId = action.payload.locationId;
        const locations = state.get('locations');
        const filteredLocations = locations.filter((loc) => loc.get('id') !== locationId);

        return state.merge({
            deleting: false,
            deleteError: null,
            locations: filteredLocations,
            total: Math.max(0, state.get('total') - 1),
        });
    }

    case types.AGENCY_LOCATION_DELETE_FAILURE:
        return state.merge({
            deleting: false,
            deleteError: action.payload,
        });

    default:
        return state;
    }
};

// Selectors
const getAgencyLocationsData = (state) => state.getIn(['app', 'agencyLocations']);

export const selectors = {
    getAgencyLocationsData,
    getAgencyLocations: createSelector(
        getAgencyLocationsData,
        (data) => data.get('locations', fromJS([])).toJS(),
    ),
    isAgencyLocationsLoading: createSelector(
        getAgencyLocationsData,
        (data) => data.get('loading', false),
    ),
    getAgencyLocationsError: createSelector(
        getAgencyLocationsData,
        (data) => data.get('error', null),
    ),
    getAgencyLocationsTotal: createSelector(
        getAgencyLocationsData,
        (data) => data.get('total', 0),
    ),
    getAgencyLocationsPage: createSelector(
        getAgencyLocationsData,
        (data) => data.get('page', 1),
    ),
    getAgencyLocationsPerPage: createSelector(
        getAgencyLocationsData,
        (data) => data.get('perPage', 20),
    ),

    // CRUD operation selectors
    isCreatingAgencyLocation: createSelector(
        getAgencyLocationsData,
        (data) => data.get('creating', false),
    ),
    getCreateAgencyLocationError: createSelector(
        getAgencyLocationsData,
        (data) => data.get('createError', null),
    ),
    isUpdatingAgencyLocation: createSelector(
        getAgencyLocationsData,
        (data) => data.get('updating', false),
    ),
    getUpdateAgencyLocationError: createSelector(
        getAgencyLocationsData,
        (data) => data.get('updateError', null),
    ),
    isDeletingAgencyLocation: createSelector(
        getAgencyLocationsData,
        (data) => data.get('deleting', false),
    ),
    getDeleteAgencyLocationError: createSelector(
        getAgencyLocationsData,
        (data) => data.get('deleteError', null),
    ),
};

export default agencyLocations;
